import { DocumentPreflight } from 'models/library/documentPreflight';
import { SkillRequirementFitItem } from 'models/skills/skillRequirementsFit';

//
// The organization's Capabilities (spec 15, 16): an approved MCP server or HTTPS API. The reviewed configuration lives
// on immutable versions approved by another administrator; the observed state (server info, the discovered tool
// catalog, the last test) lives on the capability. A user connects their own account to a capability that needs one.
// The enums mirror the API's by number.
//

export enum CapabilityType {
  Mcp = 0,
  Https = 1,
}

export enum CapabilityStatus {
  Active = 0,
  Disabled = 1,
}

// Draft -> Approved (in force) -> Superseded (by the next approval)
export enum CapabilityVersionStatus {
  Draft = 0,
  Approved = 1,
  Superseded = 2,
}

export enum McpTransportMode {
  AutoDetect = 0,
  StreamableHttp = 1,
  Sse = 2,
}

export enum CapabilityAuthMode {
  None = 0,
  // one organization credential in a fixed header, for every user
  StaticHeader = 1,
  // each user connects their own account through the provider's consent page
  OAuthAuthorizationCode = 2,
  // each user pastes a personal API token
  UserApiToken = 3,
  // one organization credential, recorded as Shared on every call (spec 16.2)
  SharedCredential = 4,
}

// The signed-in user's own state on a capability (GET capabilities/mine)
export enum CapabilityConnectionStatus {
  Connected = 0,
  AuthorizationRequired = 1,
  NotRequired = 2,
  NeedsReauthorization = 3,
  NotConfigured = 4,
}

// The key: lowercase letters, digits and single hyphens (the API's IdPattern), at most 16 characters
export const capabilityKeyPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const capabilityKeyMaxLength = 16;

// The field lengths the API accepts (Capability, CapabilityVersion, CapabilityValidator)
export const capabilityNameMaxLength = 128;
export const capabilityDescriptionMaxLength = 512;
export const capabilityEndpointMaxLength = 512;
export const capabilityChangeSummaryMaxLength = 1024;
export const capabilityHeaderNameMaxLength = 64;
export const capabilityHeaderValueMaxLength = 4096;
export const capabilityClientIdMaxLength = 256;
export const capabilityClientSecretMaxLength = 1024;
export const capabilityScopesMaxLength = 512;
export const capabilityOperationMaxCount = 50;
export const capabilityOperationPathMaxLength = 256;
export const capabilityMaxCallsLimit = 1000;
// A personal token of UserApiToken (the API's CAP0056 limit)
export const capabilityTokenMaxLength = 4096;

// The help link of a capability (phase 55): an https address of at most this many characters
export const capabilityDocsUrlMaxLength = 512;

// The service principal's own OAuth client as the API accepts it (phase 51): the id and the secret
export const servicePrincipalClientIdMaxLength = 256;

export const servicePrincipalClientSecretMaxLength = 4096;

// Mirrored by numeric value from the API (CredentialGrantKind, phase 51): how the stored credential signs in
export enum CredentialGrantKind {
  // the own sign-in or the own token (also of a service principal's app password)
  AuthorizationCode = 0,
  // a service principal's client id and secret
  ClientCredentials = 1,
}

export const capabilityOperationMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// The auth modes that run with the user's own credential, connected in Connected accounts
export const needsUserCredential = (authMode: CapabilityAuthMode | undefined): boolean =>
  authMode === CapabilityAuthMode.OAuthAuthorizationCode || authMode === CapabilityAuthMode.UserApiToken;

// The auth modes that run with one organization credential for every user (D5)
export const usesOrganizationCredential = (authMode: CapabilityAuthMode | undefined): boolean =>
  authMode === CapabilityAuthMode.StaticHeader || authMode === CapabilityAuthMode.SharedCredential;

// The auth modes that store a header value on the version
export const usesHeaderValue = usesOrganizationCredential;

// The auth modes that send a header whose name the administrator sets
export const usesHeaderName = (authMode: CapabilityAuthMode | undefined): boolean =>
  usesOrganizationCredential(authMode) || authMode === CapabilityAuthMode.UserApiToken;

// One allowed operation of an HTTPS version: a method and a path pattern whose '*' segment matches one segment
export class CapabilityOperation {
  method: string;

  path: string;

  constructor(method?: string, path?: string) {
    this.method = method ?? 'GET';
    this.path = path ?? '/';
  }

  isEqual(other: CapabilityOperation): boolean {
    return this.method === other.method && this.path === other.path;
  }
}

// One enabled tool of an MCP version
export class CapabilityAllowedTool {
  remoteName: string;

  exposedName: string;

  // the tool was annotated read-only when it was enabled
  readOnly: boolean;

  // the administrator enabled a tool that is not annotated read-only
  enabledOverride: boolean;

  constructor() {
    this.remoteName = '';
    this.exposedName = '';
    this.readOnly = false;
    this.enabledOverride = false;
  }
}

// One tool of the discovered MCP catalog with its enablement on the latest version
export class CapabilityTool {
  remoteName: string;

  // the name the model sees: mcp_{key}_{remote name}
  exposedName: string;

  title: string;

  description: string;

  inputSchema: string;

  // the MCP annotations; undefined when the server gives none
  readOnlyHint?: boolean;

  destructiveHint?: boolean;

  enabled: boolean;

  enabledOverride: boolean;

  constructor() {
    this.remoteName = '';
    this.exposedName = '';
    this.title = '';
    this.description = '';
    this.inputSchema = '';
    this.enabled = false;
    this.enabledOverride = false;
  }

  clone(): CapabilityTool {
    return Object.assign(new CapabilityTool(), this);
  }
}

// A change of the enablement of one tool (PUT capabilities/{id}/tools)
export class CapabilityToolEnablement {
  remoteName: string;

  enabled: boolean;

  // required to enable a tool that is not annotated read-only
  enabledOverride: boolean;

  constructor(remoteName?: string, enabled?: boolean, enabledOverride?: boolean) {
    this.remoteName = remoteName ?? '';
    this.enabled = enabled ?? false;
    this.enabledOverride = enabledOverride ?? false;
  }
}

// One reviewed configuration of a capability; immutable once it left Draft. Secrets are never sent back.
export class CapabilityVersion {
  capabilityId: string;

  capabilityVersionId: string;

  versionNo: number;

  status: CapabilityVersionStatus;

  // the MCP endpoint or the HTTPS base URL
  endpoint: string;

  transportMode: McpTransportMode;

  authMode: CapabilityAuthMode;

  headerName: string;

  hasHeaderValue: boolean;

  // the last four characters of the stored header value
  secretHint?: string;

  oAuthClientId: string;

  hasOAuthClientSecret: boolean;

  oAuthScopes: string;

  // empty: discovered at the first authorization
  authorizationEndpoint: string;

  tokenEndpoint: string;

  resourceIndicator: string;

  endpointsDiscoveredUtc?: Date;

  // when SkillControl registered its own OAuth client at the server (RFC 7591); empty for an admin-entered client id
  oAuthClientRegisteredUtc?: Date;

  // 'entra' or 'github': the sign-in uses SkillControl's own OAuth client (phase 45), so there is no client to enter
  platformClientKey?: string;

  allowedTools: CapabilityAllowedTool[];

  allowedOperations: CapabilityOperation[];

  // D6: writes pass only when this is on and the Skill's permissions grant write
  allowWrite: boolean;

  maxCallsPerExecution?: number;

  changeSummary: string;

  authorUserId: string;

  approvedByUserId?: string;

  approvedAt?: Date;

  created: Date;

  constructor() {
    this.capabilityId = '';
    this.capabilityVersionId = '';
    this.versionNo = 0;
    this.status = CapabilityVersionStatus.Draft;
    this.endpoint = '';
    this.transportMode = McpTransportMode.AutoDetect;
    this.authMode = CapabilityAuthMode.None;
    this.headerName = '';
    this.hasHeaderValue = false;
    this.oAuthClientId = '';
    this.hasOAuthClientSecret = false;
    this.oAuthScopes = '';
    this.authorizationEndpoint = '';
    this.tokenEndpoint = '';
    this.resourceIndicator = '';
    this.allowedTools = [];
    this.allowedOperations = [];
    this.allowWrite = false;
    this.changeSummary = '';
    this.authorUserId = '';
    this.created = new Date();
  }
}

// One capability of the admin list and the head of the detail
export class Capability {
  capabilityId: string;

  // the slug a skill.yaml names under requires.capabilities; never changes
  key: string;

  name: string;

  description: string;

  // the help link (phase 55); empty when none is set, and then nothing shows it
  docsUrl: string;

  type: CapabilityType;

  status: CapabilityStatus;

  // the Approved version in force; undefined before the first approval
  publishedVersionId?: string;

  publishedVersionNo?: number;

  // the newest version: the draft when there is one, otherwise the version in force
  latestVersionId?: string;

  latestVersionNo?: number;

  latestVersionStatus?: CapabilityVersionStatus;

  serverName: string;

  serverVersion: string;

  protocolVersion: string;

  toolsDiscoveredUtc?: Date;

  lastTestUtc?: Date;

  lastTestSucceeded?: boolean;

  // a fixed sentence of the API, never the remote server's text
  lastTestMessage: string;

  createdById: string;

  created: Date;

  constructor() {
    this.capabilityId = '';
    this.key = '';
    this.name = '';
    this.description = '';
    this.docsUrl = '';
    this.type = CapabilityType.Mcp;
    this.status = CapabilityStatus.Active;
    this.serverName = '';
    this.serverVersion = '';
    this.protocolVersion = '';
    this.lastTestMessage = '';
    this.createdById = '';
    this.created = new Date();
  }

  hasDraft(): boolean {
    return this.latestVersionStatus === CapabilityVersionStatus.Draft;
  }
}

// GET capabilities/{id}: the capability with every version (newest first) and the discovered tool catalog
export class CapabilityDetail extends Capability {
  versions: CapabilityVersion[];

  // MCP: the discovered catalog with the enablement of the latest version
  tools: CapabilityTool[];

  // the OAuth redirect URI of this environment, to register with the provider
  oAuthRedirectUri: string;

  constructor() {
    super();
    this.versions = [];
    this.tools = [];
    this.oAuthRedirectUri = '';
  }

  getLatestVersion(): CapabilityVersion | undefined {
    return this.versions.find((version) => version.capabilityVersionId === this.latestVersionId) ?? this.versions[0];
  }

  getDraftVersion(): CapabilityVersion | undefined {
    return this.versions.find((version) => version.status === CapabilityVersionStatus.Draft);
  }
}

// The configuration of a draft as the administrator edits it. An empty headerValue or oAuthClientSecret keeps the
// stored value.
export class CapabilityVersionConfig {
  endpoint: string;

  transportMode: McpTransportMode;

  authMode: CapabilityAuthMode;

  headerName: string;

  headerValue: string;

  oAuthClientId: string;

  oAuthClientSecret: string;

  oAuthScopes: string;

  authorizationEndpoint: string;

  tokenEndpoint: string;

  resourceIndicator: string;

  allowedOperations: CapabilityOperation[];

  allowWrite: boolean;

  maxCallsPerExecution?: number;

  changeSummary: string;

  constructor() {
    this.endpoint = '';
    this.transportMode = McpTransportMode.AutoDetect;
    this.authMode = CapabilityAuthMode.OAuthAuthorizationCode;
    this.headerName = '';
    this.headerValue = '';
    this.oAuthClientId = '';
    this.oAuthClientSecret = '';
    this.oAuthScopes = '';
    this.authorizationEndpoint = '';
    this.tokenEndpoint = '';
    this.resourceIndicator = '';
    this.allowedOperations = [];
    this.allowWrite = false;
    this.changeSummary = '';
  }

  static fromVersion(version: CapabilityVersion): CapabilityVersionConfig {
    const output = new CapabilityVersionConfig();
    output.endpoint = version.endpoint;
    output.transportMode = version.transportMode;
    output.authMode = version.authMode;
    output.headerName = version.headerName;
    output.oAuthClientId = version.oAuthClientId;
    output.oAuthScopes = version.oAuthScopes;
    output.authorizationEndpoint = version.authorizationEndpoint;
    output.tokenEndpoint = version.tokenEndpoint;
    output.resourceIndicator = version.resourceIndicator;
    output.allowedOperations = version.allowedOperations.map((item) => new CapabilityOperation(item.method, item.path));
    output.allowWrite = version.allowWrite;
    output.maxCallsPerExecution = version.maxCallsPerExecution;
    output.changeSummary = version.changeSummary;

    return output;
  }

  clone(): CapabilityVersionConfig {
    const output = Object.assign(new CapabilityVersionConfig(), this);
    output.allowedOperations = this.allowedOperations.map((item) => new CapabilityOperation(item.method, item.path));

    return output;
  }

  isEqual(other: CapabilityVersionConfig): boolean {
    return (
      this.endpoint === other.endpoint &&
      this.transportMode === other.transportMode &&
      this.authMode === other.authMode &&
      this.headerName === other.headerName &&
      this.headerValue === other.headerValue &&
      this.oAuthClientId === other.oAuthClientId &&
      this.oAuthClientSecret === other.oAuthClientSecret &&
      this.oAuthScopes === other.oAuthScopes &&
      this.authorizationEndpoint === other.authorizationEndpoint &&
      this.tokenEndpoint === other.tokenEndpoint &&
      this.resourceIndicator === other.resourceIndicator &&
      this.allowWrite === other.allowWrite &&
      this.maxCallsPerExecution === other.maxCallsPerExecution &&
      this.changeSummary === other.changeSummary &&
      this.allowedOperations.length === other.allowedOperations.length &&
      this.allowedOperations.every((item, index) => item.isEqual(other.allowedOperations[index]))
    );
  }
}

// POST capabilities: the capability and the configuration of its draft version 1
export class NewCapability extends CapabilityVersionConfig {
  // empty derives the key from the name
  key: string;

  name: string;

  description: string;

  // the help link (phase 55); empty sets none
  docsUrl: string;

  type: CapabilityType;

  // the catalog entry the form was pre-filled from (phase 45: its platform client, if it has one)
  capabilityCatalogEntryId?: string;

  constructor() {
    super();
    this.key = '';
    this.name = '';
    this.description = '';
    this.docsUrl = '';
    this.type = CapabilityType.Mcp;
  }
}

// The fields PUT capabilities/{id} writes; the key never changes
export class CapabilityDetails {
  name: string;

  description: string;

  // the help link (phase 55): metadata like the description, so no new version
  docsUrl: string;

  constructor() {
    this.name = '';
    this.description = '';
    this.docsUrl = '';
  }

  static fromCapability(capability: Capability): CapabilityDetails {
    const output = new CapabilityDetails();
    output.name = capability.name;
    output.description = capability.description;
    output.docsUrl = capability.docsUrl;

    return output;
  }

  isEqual(other: CapabilityDetails): boolean {
    return this.name === other.name && this.description === other.description && this.docsUrl === other.docsUrl;
  }
}

// POST capabilities/{id}/test. A remote failure is a result with succeeded false and a fixed sentence in message.
export class CapabilityTestResult {
  succeeded: boolean;

  message: string;

  serverName: string;

  serverVersion: string;

  protocolVersion: string;

  toolCount: number;

  durationMs: number;

  testedUtc: Date;

  // the version whose configuration was tested: the draft when there is one
  capabilityVersionId: string;

  versionNo: number;

  constructor() {
    this.succeeded = false;
    this.message = '';
    this.serverName = '';
    this.serverVersion = '';
    this.protocolVersion = '';
    this.toolCount = 0;
    this.durationMs = 0;
    this.testedUtc = new Date();
    this.capabilityVersionId = '';
    this.versionNo = 0;
  }
}

// POST capabilities/{id}/discover: the outcome and the capability with the refreshed catalog
export class CapabilityDiscoveryResult {
  succeeded: boolean;

  message: string;

  toolCount: number;

  // the enabled tools of the latest version after the merge
  enabledToolCount: number;

  capability?: CapabilityDetail;

  constructor() {
    this.succeeded = false;
    this.message = '';
    this.toolCount = 0;
    this.enabledToolCount = 0;
  }
}

// One entry of a capability's change log, newest first
export class CapabilityAuditEntry {
  auditTrailEntryId: number;

  auditTrailActionId: number;

  // ATA-CA-001 .. ATA-CA-007
  actionCode: string;

  // the action's label in the caller's language
  action: string;

  created: Date;

  userId: string;

  userName: string;

  // Version, Status, EnabledTools or SharedCredential; empty when the action names no property
  property: string;

  oldValue: string;

  newValue: string;

  constructor() {
    this.auditTrailEntryId = 0;
    this.auditTrailActionId = 0;
    this.actionCode = '';
    this.action = '';
    this.created = new Date();
    this.userId = '';
    this.userName = '';
    this.property = '';
    this.oldValue = '';
    this.newValue = '';
  }
}

// One Active capability with an approved version as the signed-in user sees it; never a token or a secret
export class CapabilityConnection {
  capabilityId: string;

  key: string;

  name: string;

  description: string;

  type: CapabilityType;

  authMode: CapabilityAuthMode;

  status: CapabilityConnectionStatus;

  // calls run with one organization credential instead of the user's own (D5)
  usesOrganizationCredential: boolean;

  // when the user connected; only while Connected
  authorizedUtc?: Date;

  // a fixed sentence of the API about why the credential stopped working
  lastError: string;

  grantKind: CredentialGrantKind;

  constructor() {
    this.capabilityId = '';
    this.key = '';
    this.name = '';
    this.description = '';
    this.type = CapabilityType.Mcp;
    this.authMode = CapabilityAuthMode.None;
    this.status = CapabilityConnectionStatus.NotRequired;
    this.usesOrganizationCredential = false;
    this.lastError = '';
    this.grantKind = CredentialGrantKind.AuthorizationCode;
  }
}

// One capability a Skill version requires (requires.capabilities) with the signed-in user's status on it: a line of
// the preflight of spec 16.4
export class CapabilityPreflight {
  // the key as the Skill names it
  capabilityKey: string;

  // undefined when no Active capability with an approved version carries the key (NotConfigured)
  capabilityId?: string;

  // the capability's name; the key when it is not configured
  name: string;

  status: CapabilityConnectionStatus;

  // the auth mode of the version in force; undefined when not configured. OAuth connects in a popup, UserApiToken
  // through the token entry
  authMode?: CapabilityAuthMode;

  // the capability's help link (phase 55); empty when none is set
  docsUrl: string;

  // calls run with one organization credential instead of the user's own (D5)
  usesOrganizationCredential: boolean;

  // the Skill's permissions match nothing this capability offers: the run would get no tool from it. A warning on
  // the card; the run is not refused
  noCallableTools: boolean;

  // what the Skill names under this key that the capability does not provide (phase 11, the requirements fit): tools
  // not enabled or not on the server, operations no allowed path carries, or the capability itself. Refuses the run
  unsatisfiedRequirements: SkillRequirementFitItem[];

  constructor() {
    this.capabilityKey = '';
    this.name = '';
    this.docsUrl = '';
    this.status = CapabilityConnectionStatus.NotConfigured;
    this.usesOrganizationCredential = false;
    this.noCallableTools = false;
    this.unsatisfiedRequirements = [];
  }
}

// The preflight of a Skill's version for the signed-in user (GET skills/{id}/preflight, and the route answer of a
// match). The API accepts a run with the version only while ready (CAP0109, CAP0110, CAP0111 otherwise).
export class SkillPreflight {
  skillId: string;

  skillVersionId: string;

  // every required capability is Connected or NotRequired, and the requirements fit is satisfied
  ready: boolean;

  // the organization's capabilities provide every tool and operation the version names (phase 11)
  requirementsSatisfied: boolean;

  // in the order of requires.capabilities; empty when the version requires none
  capabilities: CapabilityPreflight[];

  // the documents block's entries for the signed-in user (phase 28); empty when the version declares none
  documents: DocumentPreflight[];

  // notes about the run (phase 59, a built-in tool the organization's model does not provide); never part of ready
  requirementNotes: SkillRequirementFitItem[];

  constructor() {
    this.skillId = '';
    this.skillVersionId = '';
    this.ready = true;
    this.requirementsSatisfied = true;
    this.capabilities = [];
    this.documents = [];
    this.requirementNotes = [];
  }
}

// The connection statuses in which the user has to connect their own account
export const isConnectNeededStatus = (status: CapabilityConnectionStatus): boolean =>
  status === CapabilityConnectionStatus.AuthorizationRequired ||
  status === CapabilityConnectionStatus.NeedsReauthorization;
